import { inject, Injectable, Signal, signal } from '@angular/core';
import * as Auth from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { AwsCognitoConstants } from '../../constants/aws.cognito.constants';
import { AuthWrapper } from './auth-wrapper';

type Groups = string[];

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authWrapper = inject(AuthWrapper);
  private readonly groupsSig = signal<Groups>([]);

  constructor() {
    this.refreshGroups();

    Hub.listen('auth', ({ payload }) => {
      const evt = payload?.event as string | undefined;
      if (evt === 'signedIn' || evt === 'tokenRefresh' || evt === 'signedOut') {
        this.refreshGroups();
      }
    });
  }

  hasGroup = (group: string): boolean => this.groupsSig().includes(group);

  get groups(): Signal<Groups> {
    return this.groupsSig.asReadonly();
  }

  async registerUser(input: {
    fullName: string;
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    userId?: string;
    userName?: string;
  }): Promise<string> {
    const username = `user_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

    return await this.createAuthOperation(async () => {
      await this.authWrapper.signUp({
        // Username must not be email when pool uses email alias
        username,
        password: input.password,
        options: {
          userAttributes: {
            name: input.fullName,
            email: input.email,
          },
        },
      });

      // Persist the actual Cognito username only on successful sign-up.
      localStorage.setItem('lastSignUpUsername', username);

      return username;
    }, 'Error during user registration:');
  }

  // Initiate Google sign-in via Cognito Social IdP
  async signInWithGoogle(): Promise<void> {
    localStorage.setItem('socialSignInPending', 'google');
    await this.authWrapper.signInWithGoogleRedirect();
  }

  // Initiate LinkedIn sign-in via Cognito OIDC IdP
  async signInWithLinkedIn(): Promise<void> {
    localStorage.setItem('socialSignInPending', 'linkedin');
    await this.authWrapper.signInWithLinkedInRedirect();
  }

  // Initiate Microsoft sign-in via Cognito OIDC IdP
  async signInWithMicrosoft(): Promise<void> {
    localStorage.setItem('socialSignInPending', 'microsoft');
    await this.authWrapper.signInWithMicrosoftRedirect();
  }

  async confirmSignUp(input: Auth.ConfirmSignUpInput): Promise<Auth.ConfirmSignUpOutput> {
    return await this.createAuthOperation(
      async () => await this.authWrapper.confirmSignUp(input),
      'Error during confirm sign up:',
    );
  }

  async signIn(input: Auth.SignInInput): Promise<Auth.SignInOutput> {
    return await this.createAuthOperation(
      async () => await this.authWrapper.signIn(input),
      'Error during sign in:',
    );
  }

  async resendSignUpCode(input: Auth.ResendSignUpCodeInput): Promise<Auth.ResendSignUpCodeOutput> {
    return await this.createAuthOperation(
      async () => await this.authWrapper.resendSignUpCode(input),
      'Error during resend sign up code:',
    );
  }

  async confirmResetPassword(input: Auth.ConfirmResetPasswordInput): Promise<void> {
    return await this.createAuthOperation(
      async () => await this.authWrapper.confirmResetPassword(input),
      'Error during confirm reset password:',
    );
  }

  async signUp(input: Auth.SignUpInput): Promise<Auth.SignUpOutput> {
    return await this.createAuthOperation(
      async () => await this.authWrapper.signUp(input),
      'Error during sign up:',
    );
  }

  async resetPassword(input: Auth.ResetPasswordInput): Promise<Auth.ResetPasswordOutput> {
    return await this.createAuthOperation(
      async () => await this.authWrapper.resetPassword(input),
      'Error during reset password:',
    );
  }

  async getCurrentUser(): Promise<Auth.AuthUser> {
    return await this.createAuthOperation(
      async () => await this.authWrapper.getCurrentUser(),
      'Error during get current user:',
    );
  }

  async getIdToken(): Promise<string | null> {
    try {
      return await this.authWrapper.getIdToken();
    } catch (error: any) {
      if (error.name === AwsCognitoConstants.UserUnAuthenticatedException) {
        return null;
      }
      throw error;
    }
  }

  async signOut(input?: Auth.SignOutInput): Promise<void> {
    return await this.createAuthOperation(
      async () => await this.authWrapper.signOut(input),
      'Error during sign out:',
    );
  }

  async isLoginOn(): Promise<boolean> {
    try {
      const user = await this.authWrapper.getCurrentUser();
      return user.userId != null;
    } catch (error: any) {
      if (error.name === AwsCognitoConstants.UserUnAuthenticatedException) {
        return false;
      }
      throw error;
    }
  }

  // --- MFA / TOTP helpers ---

  async fetchMfaPreference(): Promise<Auth.FetchMFAPreferenceOutput | null> {
    try {
      return await Auth.fetchMFAPreference();
    } catch (error: any) {
      if (
        error?.name === 'NotAuthorizedException' &&
        typeof error?.message === 'string' &&
        error.message.includes('Access Token does not have required scopes')
      ) {
        // MFA helper APIs require additional OAuth scopes.
        // If they are not configured yet, treat MFA as disabled without logging.
        return null;
      }
      this.logError('Error during fetch MFA preference:', error);
      throw error;
    }
  }

  async setUpTotp(): Promise<Auth.SetUpTOTPOutput> {
    return await this.createAuthOperation(
      async () => await Auth.setUpTOTP(),
      'Error during TOTP setup:',
    );
  }

  async verifyTotpSetup(code: string): Promise<void> {
    return await this.createAuthOperation(
      async () => await Auth.verifyTOTPSetup({ code }),
      'Error during TOTP verification:',
    );
  }

  async updateMfaPreference(input: Auth.UpdateMFAPreferenceInput): Promise<void> {
    return await this.createAuthOperation(
      async () => await Auth.updateMFAPreference(input),
      'Error during update MFA preference:',
    );
  }

  async confirmSignInWithMfaCode(code: string): Promise<Auth.ConfirmSignInOutput> {
    return await this.createAuthOperation(
      async () => await Auth.confirmSignIn({ challengeResponse: code }),
      'Error during confirm sign in:',
    );
  }

  private async createAuthOperation<T>(
    operation: () => Promise<T>,
    errorMessage: string,
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      this.logError(errorMessage, error);
      throw error;
    }
  }

  private async refreshGroups(): Promise<void> {
    try {
      const session = await Auth.fetchAuthSession();
      const claims = session.tokens?.idToken?.payload ?? {};
      const groups = (claims['cognito:groups'] as string[] | undefined) ?? [];
      this.groupsSig.set(groups);
    } catch {
      this.groupsSig.set([]);
    }
  }

  private logError(message: string, error: unknown): void {
    console.error(message, error);
  }
}
